import { Box, Divider, Stack, styled, TooltipProps, Typography } from '@mui/material';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ReactNode, useMemo } from 'react';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { useDisplayTime } from 'src/utils/hooks';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);
dayjs.extend(duration);

const TimeZoneTooltipContainer = styled(Box, {
  name: 'TimeZoneTooltipContainer',
})(({ theme }) => ({
  margin: 0,
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  width: '22rem',
  boxShadow: theme.shadows[1],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

const TimeZoneLabel = styled(Typography, {
  name: 'TimeZoneLabel',
})(() => ({
  width: '55%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer',
}));

const ChildrenContainer = styled('span', {
  name: 'ChildrenContainer',
})(() => ({
  fontSize: '0.6875rem',
  fontWeight: 700,
  cursor: 'pointer',
}));

export interface TimeZoneTooltipProps {
  date: string;
  tzList: {
    tz: string;
    label: string;
    timeFormat?: string;
    dateFormat?: string;
  }[];

  tooltipProps?: Partial<TooltipProps>;
  children?: ReactNode;
  title?: string;
}

export const TimeZoneTooltip = ({
  children,
  date,
  title = 'Time Conversion',
  tzList,
  tooltipProps,
}: TimeZoneTooltipProps) => {
  const { timeFormat } = useDisplayTime();

  const formattedRows = useMemo(
    () =>
      tzList.map((i) => {
        const dateObj = dayjs(date).tz(i.tz);

        return {
          tz: i.tz,
          label: `${i.label} • ${dateObj.format('z')}`,
          timeInTz: dateObj.format(i.timeFormat ?? timeFormat ?? 'HH:mm'),
          dateInTz: dateObj.format(i.dateFormat ?? 'MMM D, YYYY'),
        };
      }),
    [date, timeFormat, tzList],
  );

  return (
    <Tooltip
      componentsProps={{
        // MUI's default 300px maxWidth is narrower than the 22rem card, so the popper
        // measured a clipped box and let the card overflow the viewport.
        tooltip: { sx: { padding: 0, maxWidth: 'none' } },
      }}
      tapToOpenOnMobile
      {...tooltipProps}
      title={
        <TimeZoneTooltipContainer>
          <Typography variant="subtitle2">{title}</Typography>
          <Divider sx={{ margin: '0.8rem 0' }} />
          <Stack gap="1rem">
            {formattedRows.map((i) => (
              <Stack key={i.label} direction="row" justifyContent="space-between">
                <TimeZoneLabel variant="body2">{i.label}</TimeZoneLabel>
                <Typography
                  variant="body2"
                  sx={{ paddingLeft: '1rem', paddingRight: '1rem', whiteSpace: 'nowrap' }}
                >
                  {i.timeInTz}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ width: 'fit-content', whiteSpace: 'nowrap' }}
                >
                  {i.dateInTz}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </TimeZoneTooltipContainer>
      }
    >
      <ChildrenContainer>
        {children ? children : dayjs(date).format(timeFormat ?? 'HH:mm A')}
      </ChildrenContainer>
    </Tooltip>
  );
};

export default TimeZoneTooltip;
